import { getAttribute, resolveAttribute, resolveTriState } from '@citiguide/shared';
import type { AttributePath, AttributeSource, AttributeValue, Marker, OpeningPeriod } from '@citiguide/shared';

export interface PoiAttributeRow {
  key: 'price' | 'hours' | 'accessibility';
  label: string;
  text: string;
  /** Source of the displayed value; 'category-default' means "usually", not a fact about this place. */
  source?: AttributeSource;
}

type MarkerAttrs = Pick<Marker, 'attributes' | 'openingHours'>;

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON_FIRST = [1, 2, 3, 4, 5, 6, 0];

function priceRow(marker: MarkerAttrs): PoiAttributeRow | null {
  const level = resolveAttribute(marker, 'price.level');
  if (!level || typeof level.value !== 'number') return null;
  const base = level.value === 0 ? 'Free' : '€'.repeat(Math.min(4, Math.max(1, level.value)));
  const from = resolveAttribute(marker, 'price.from');
  const text = from && typeof from.value === 'number' ? `${base} · from ${from.value} EUR` : base;
  return { key: 'price', label: 'Price', text, source: level.source };
}

/** 'Mon-Fri 09:00-17:00 · Sat 10:00-14:00' — consecutive days with identical times are grouped. */
export function formatOpeningPeriods(periods: OpeningPeriod[]): string {
  const byDay = new Map<number, string>();
  for (const p of periods) {
    if (typeof p?.day !== 'number' || !p.open || !p.close) continue;
    const slot = `${p.open}-${p.close}`;
    byDay.set(p.day, byDay.has(p.day) ? `${byDay.get(p.day)}, ${slot}` : slot);
  }
  const groups: { days: number[]; times: string }[] = [];
  for (const day of MON_FIRST) {
    const times = byDay.get(day);
    if (!times) continue;
    const last = groups[groups.length - 1];
    const prevDay = last ? last.days[last.days.length - 1] : undefined;
    const adjacent = prevDay !== undefined && MON_FIRST.indexOf(day) === MON_FIRST.indexOf(prevDay) + 1;
    if (last && adjacent && last.times === times) last.days.push(day);
    else groups.push({ days: [day], times });
  }
  return groups
    .map((g) => {
      const first = DAY_NAMES[g.days[0]];
      const label = g.days.length === 1 ? first : `${first}-${DAY_NAMES[g.days[g.days.length - 1]]}`;
      return `${label} ${g.times}`;
    })
    .join(' · ');
}

function hoursRow(marker: MarkerAttrs): PoiAttributeRow | null {
  const slot = resolveAttribute(marker, 'openingPeriods');
  if (slot && Array.isArray(slot.value)) {
    const text = formatOpeningPeriods(slot.value as OpeningPeriod[]);
    if (text) return { key: 'hours', label: 'Hours', text, source: slot.source };
  }
  const legacy = typeof marker.openingHours === 'string' ? marker.openingHours.trim() : '';
  return legacy ? { key: 'hours', label: 'Hours', text: legacy } : null;
}

const ACCESS: { path: AttributePath; yes: string; no: string }[] = [
  { path: 'accessibility.stepFree', yes: 'Step-free access', no: 'No step-free access' },
  { path: 'accessibility.wheelchair', yes: 'Wheelchair accessible', no: 'Not wheelchair accessible' },
  { path: 'accessibility.accessibleToilet', yes: 'Accessible toilet', no: 'No accessible toilet' },
  { path: 'accessibility.seating', yes: 'Seating', no: 'No seating' },
  { path: 'accessibility.lift', yes: 'Lift', no: 'No lift' },
];

function accessibilityRow(marker: MarkerAttrs): PoiAttributeRow | null {
  const parts: string[] = [];
  const slots: AttributeValue<unknown>[] = [];
  for (const { path, yes, no } of ACCESS) {
    const state = resolveTriState(marker, path);
    if (state === 'unknown') continue; // unknown is never shown as "no"
    parts.push(state ? yes : no);
    const slot = getAttribute(marker.attributes, path);
    if (slot) slots.push(slot);
  }
  const effort = resolveAttribute(marker, 'accessibility.walkingEffort');
  if (effort && typeof effort.value === 'string') {
    parts.push(`Walking effort: ${effort.value}`);
    slots.push(effort);
  }
  if (parts.length === 0) return null;
  const sources = new Set(slots.map((s) => s.source));
  return {
    key: 'accessibility',
    label: 'Accessibility',
    text: parts.join(' · '),
    source: sources.size === 1 ? slots[0].source : undefined,
  };
}

/** Rows for the "Practical info" block; empty when nothing is known. */
export function buildPoiAttributeRows(marker: MarkerAttrs | null | undefined): PoiAttributeRow[] {
  if (!marker) return [];
  return [priceRow(marker), hoursRow(marker), accessibilityRow(marker)].filter(
    (r): r is PoiAttributeRow => r !== null
  );
}

export function displayText(row: PoiAttributeRow): string {
  return row.source === 'category-default' ? `Usually: ${row.text}` : row.text;
}
